import { Head, router, useForm } from "@inertiajs/react"

import {
  EventForm,
  type EventFormData,
  type Neighborhood,
} from "@/components/events/event-form"
import AppLayout from "@/layouts/app-layout"
import { eventPath } from "@/routes"

interface EventsEditProps {
  event: {
    id: number
    title: string
    description: string
    category: string
    start_date: string | null
    end_date: string | null
    venue_name?: string
    address?: string
    latitude?: number | string | null
    longitude?: number | string | null
    neighborhood?: { id: number; name: string } | null
    city?: { id: number; name: string } | null
    price?: number | string | null
    is_free?: boolean
    capacity?: number | string | null
    visibility: string
    featured?: boolean
    image_url?: string | null
  }
  neighborhoods: Neighborhood[]
  categories: { value: string; label: string; emoji?: string }[]
}

export default function EventsEdit({
  event,
  neighborhoods,
  categories,
}: EventsEditProps) {
  const toDateTimeLocal = (value: string | null | undefined) => {
    if (!value) return ""
    const d = new Date(value)
    if (isNaN(d.getTime())) return ""
    const pad = (n: number) => n.toString().padStart(2, "0")
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
  }

  const initial: EventFormData = {
    title: event.title ?? "",
    description: event.description ?? "",
    category: event.category ?? "",
    start_date: toDateTimeLocal(event.start_date),
    end_date: toDateTimeLocal(event.end_date),
    venue_name: event.venue_name ?? "",
    address: event.address ?? "",
    latitude: (event.latitude ?? "").toString(),
    longitude: (event.longitude ?? "").toString(),
    neighborhood_id: (event.neighborhood?.id ?? "").toString(),
    city_id: (event.city?.id ?? "") as string | number,
    price: (event.price ?? "").toString(),
    is_free: Boolean(event.is_free),
    capacity: (event.capacity ?? "").toString(),
    visibility: event.visibility ?? "city",
    featured: Boolean(event.featured),
    image: null,
    image_url: event.image_url ?? "",
    remove_image: false,
  }
  const { data, setData, put, processing, errors } = useForm<{
    event: EventFormData
  }>({ event: initial })

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    put(eventPath(event.id))
  }

  const err = errors as unknown as Record<string, string>

  return (
    <AppLayout>
      <Head title={`Editar`} />

      <div className="mx-auto max-w-6xl p-4 md:p-6">
        <EventForm
          title="Editar evento"
          data={data.event}
          errors={err}
          neighborhoods={neighborhoods}
          categories={categories}
          processing={processing}
          onChange={(partial) =>
            setData("event", { ...data.event, ...partial })
          }
          onSubmit={submit}
          onCancel={() => router.visit(eventPath(event.id))}
          submitLabel="Guardar cambios"
          currentImage={event.image_url ?? null}
        />
      </div>
    </AppLayout>
  )
}
